import { KeyValue } from "@angular/common";
import { CREATE_EFFECT_METADATA_KEY } from "@ngrx/effects/src/models";
import moment from "moment";

const originalOrder = (
  _a: KeyValue<string, unknown>,
  _b: KeyValue<string, unknown>,
): number => {
  return 0;
};
const splitStringByCapitalLetter = (str): string => {
  return str.split(/(?=[A-Z])/).join(" ");
};
const agGridFormatDateTime = (param: any) => {
  if (!param.value) {
    return "";
  }
  return moment(param.value).utc().format("Y/MM/DD HH:mm");
};
const agGridFormatDuration = (param: any) => {
  if (!param.value) {
    return "";
  }
  return moment(param.value).utc().format(`HH:mm:ss.SSS`);
};

const unixTimestampToFormattedTimecode = (
  unixTimestamp: number,
  fps: number,
) => {
  const date = new Date(unixTimestamp);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");

  const seconds = date.getUTCSeconds().toString().padStart(2, "0");
  const frames = Math.floor((date.getUTCMilliseconds() / 1000) * fps)
    .toString()
    .padStart(2, "0");
  return `${hours}:${minutes}:${seconds}.${frames}`;
};

const unixTimestampToFormattedTime = (
  unixTimestamp: number,
  justMinutesAndSeconds: boolean = false,
) => {
  const date = new Date(unixTimestamp);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");

  if (justMinutesAndSeconds) {
    return `${hours}:${minutes}`;
  }
  const seconds = date.getUTCSeconds().toString().padStart(2, "0");
  const milliseconds = date.getUTCMilliseconds().toString().padStart(3, "0");
  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const millisecondsToFormattedTimecode = (milliseconds: number, fps: number) => {
  // Convert milliseconds to hours, minutes, seconds, and remaining milliseconds
  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor(((milliseconds % 3600000) % 60000) / 1000);

  // Calculate the frame number based on the remaining milliseconds
  const remainingMilliseconds = ((milliseconds % 3600000) % 60000) % 1000;
  const frames = Math.floor((remainingMilliseconds / 1000) * fps);

  // Format each component as a two-digit string
  const hoursFormatted = String(hours).padStart(2, "0");
  const minutesFormatted = String(minutes).padStart(2, "0");
  const secondsFormatted = String(seconds).padStart(2, "0");
  const framesFormatted = String(frames).padStart(2, "0");

  // Combine the formatted components
  return `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}:${framesFormatted}`;
};

const millisecondsToFormattedTimestamp = (
  milliseconds: number,
  justMinutesAndSeconds: boolean = false,
) => {
  // Convert milliseconds to hours, minutes, seconds, and remaining milliseconds
  const hours = Math.floor(milliseconds / 3600000);
  const minutes =
    Math.floor((milliseconds % 3600000) / 60000) +
    (justMinutesAndSeconds ? hours * 60 : 0);
  const seconds = Math.floor(((milliseconds % 3600000) % 60000) / 1000);
  const remainingMilliseconds = Math.floor(
    ((milliseconds % 3600000) % 60000) % 1000,
  );
  // Format each component as a two-digit string
  const hoursFormatted = String(hours).padStart(2, "0");
  const minutesFormatted = String(minutes).padStart(2, "0");
  const secondsFormatted = String(seconds).padStart(2, "0");
  const millisecondsFormatted = String(remainingMilliseconds).padStart(3, "0");

  if (justMinutesAndSeconds) {
    return `${minutesFormatted}:${secondsFormatted}`;
  }
  // Combine the formatted components
  return `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}.${millisecondsFormatted}`;
};

const formatNumber = (number) => {
  // Step 1: Split the number into integer and fractional parts
  const [integerPart, fractionalPart] = number.toString().split(".");

  // Step 2: Process the fractional part
  // Convert the fractional part to a string, take the first three characters,
  // and remove any trailing zeros
  let formattedFractionalPart = fractionalPart
    ? fractionalPart.slice(0, 3).replace(/0+$/, "")
    : "000";

  // Ensure the formatted fractional part has at least three digits
  if (formattedFractionalPart.length < 3) {
    formattedFractionalPart += "0".repeat(3 - formattedFractionalPart.length);
  }

  // Step 3: Reconstruct the number
  return parseFloat(integerPart + "." + formattedFractionalPart);
};

const getEnumNameByValue = (
  e: any,
  value: number,
  splitOnUpperCaseLetter = false,
): string | undefined => {
  for (const key in e) {
    if (isNaN(Number(key))) continue; // Skip non-numeric keys
    const enumKey = Number(key);
    if (enumKey === value) {
      const val = e[key] as string;
      return splitOnUpperCaseLetter ? splitStringByUppercaseLetters(val) : val;
    }
  }
  return undefined; // Return undefined if no match is found
};
const splitStringByUppercaseLetters = (str: string) => {
  return str
    .match(/([A-Z]?[^A-Z]*)/g)
    .slice(0, -1)
    .join(" ");
};

export {
  originalOrder as KeepOriginalOrder,
  splitStringByCapitalLetter as SplitStringByCapitalLetter,
  agGridFormatDateTime as AgGridFormatDateTime,
  agGridFormatDuration as AgGridFormatDuration,
  millisecondsToFormattedTimestamp as MillisecondsToFormattedDuration,
  millisecondsToFormattedTimecode as MillisecondsToFormattedTimecode,
  getEnumNameByValue as GetEnumNameByValue,
  formatNumber as FormatNumber,
  unixTimestampToFormattedTime as UnixTimestampToFormattedTime,
  unixTimestampToFormattedTimecode as UnixTimestampToFormattedTimecode,
};
